import { useEffect, useState } from "react";
import { Wrench, PoundSterling, Handshake, Zap, Heart, Shield, Award, Sparkles, Check } from "lucide-react";
import { motion } from "framer-motion";
import { publicApi } from "@/lib/publicApi";
import { PageHero, SectionTitle } from "@/components/public/Primitives";
import TestimonialCard from "@/components/public/TestimonialCard";
import CTASection from "@/components/public/CTASection";
import SEO from "@/components/public/SEO";
import { staggerContainer, staggerChild, easeOutStrong } from "@/lib/motion";

const VALUES = [
    { icon: Wrench, title: "Practical", body: "We build tools that actually get used — not flashy demos." },
    { icon: PoundSterling, title: "Affordable", body: "Fixed prices that small UK businesses can stomach." },
    { icon: Handshake, title: "Trustworthy", body: "Plain talk, honest timelines, no jargon, no hidden fees." },
];

const WHY = [
    { icon: Zap, title: "Fast delivery", body: "Most projects shipped in 1–4 weeks, not months." },
    { icon: Heart, title: "Built for small business", body: "We understand UK takeaways, salons, retail and trades." },
    { icon: Shield, title: "Honest, capped pricing", body: "Clear quotes, no surprise invoices. We mean it." },
    { icon: Award, title: "Premium design", body: "SaaS-grade interfaces — your brand will look the part." },
    { icon: Sparkles, title: "Modern tech stack", body: "React, FastAPI, MongoDB — fast, scalable, future-proof." },
    { icon: Check, title: "30 days free support", body: "Tweaks, bug fixes, content edits — all included post-launch." },
];

const WHY_BULLETS = [
    "One team, from strategy to launch — no handoffs, no waiting on three suppliers to talk to each other.",
    "Priced up front, no surprises — you get a fixed quote before we start work, in writing, within two working days.",
    "Built to run without us — you own the code, the content, the customer data. We stay on if you want, not because you have to.",
];

export default function About() {
    const [testimonials, setTestimonials] = useState(null);
    useEffect(() => {
        publicApi.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => setTestimonials([]));
    }, []);
    const hasTestimonials = Array.isArray(testimonials) && testimonials.length > 0;
    return (
        <div data-testid="about-page">
            <SEO
                title="About Omega IT Ltd — UK IT Company for Small Businesses"
                description="Meet the UK-based team behind Omega IT Ltd. We build practical websites, apps and custom systems for ambitious UK small businesses at honest, capped prices."
            />
            <PageHero
                eyebrow="About Omega IT Ltd"
                title="A small UK IT team obsessed with small-business growth."
                subtitle="We're a UK-based digital agency that builds practical websites, apps and custom systems for ambitious small businesses. From a one-page restaurant site to a full multi-branch POS, we keep things simple, honest and fast — so you can focus on running your business while we build the digital side."
            />
            <section className="px-5 sm:px-8 lg:px-12 py-20">
                <div className="max-w-4xl mx-auto space-y-5 text-slate-700 leading-relaxed">
                    <p>Omega IT Ltd was founded with one belief: small UK businesses deserve the same quality of digital tools that big chains pay agencies tens of thousands for. So we make exactly that — properly designed, properly built, but priced for high-street takeaways, salons, trades and shops.</p>
                    <p>Our team blends product designers, software engineers and digital marketers under one roof. That means your website, your app, your branding and your SEO all speak the same language and ship in weeks, not months.</p>
                </div>
            </section>
            <section className="px-5 sm:px-8 lg:px-12 pb-20">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle eyebrow="What we stand for" title="Three values, in plain English." />
                    <div className="mt-12 grid sm:grid-cols-3 gap-5">
                        {VALUES.map(({ icon: Icon, title, body }) => (
                            <div key={title} className="bg-white border border-slate-200 rounded-2xl p-7">
                                <span className="w-11 h-11 rounded-xl bg-slate-950 text-white grid place-items-center"><Icon className="w-5 h-5" /></span>
                                <h3 className="font-display text-xl font-bold text-slate-950 mt-5">{title}</h3>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section className="px-5 sm:px-8 lg:px-12 pb-24 bg-slate-50/50 py-24">
                <div className="max-w-7xl mx-auto">
                    <SectionTitle eyebrow="Why Omega IT Ltd" title="Six reasons clients keep coming back." />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {WHY.map(({ icon: Icon, title, body }) => (
                            <div key={title} className="bg-white border border-slate-200 rounded-2xl p-6">
                                <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-950 grid place-items-center"><Icon className="w-5 h-5" /></span>
                                <h3 className="font-display text-lg font-bold text-slate-950 mt-4">{title}</h3>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section className="px-5 sm:px-8 lg:px-12 pb-24" data-testid="about-social-proof">
                {hasTestimonials ? (
                    <div className="max-w-7xl mx-auto">
                        <SectionTitle eyebrow="Real reviews" title="What our UK clients say." />
                        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {testimonials.map((t) => (<TestimonialCard key={t.id} t={t} />))}
                        </div>
                    </div>
                ) : (
                    <div className="max-w-5xl mx-auto" data-testid="about-why-fallback">
                        <SectionTitle eyebrow="— WHY OMEGA" title="Why choose Omega IT" />
                        <motion.div
                            className="mt-12 grid gap-5 sm:grid-cols-3"
                            {...staggerContainer}
                        >
                            {WHY_BULLETS.map((b, i) => (
                                <motion.div
                                    key={i}
                                    className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col"
                                    {...staggerChild}
                                >
                                    <div
                                        className="w-10 h-10 rounded-full grid place-items-center text-white font-display font-bold text-sm"
                                        style={{ background: "linear-gradient(135deg,#7C6BFF,#2DE2C6)" }}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <p className="text-sm text-slate-700 mt-5 leading-relaxed">{b}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                )}
            </section>
            <CTASection />
        </div>
    );
}
